/**
 * Step 1 — Schedule section
 *
 * Planned start / end (DateTimePicker) + traffic impact (Select). Relocated
 * from the former Time & Contractor step so the basic-info step owns the full
 * "what + when + how disruptive" block.
 */
import { memo, useMemo } from 'react';
import { Select } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { IconRouteAltLeft } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@/components/common/form';
import { ICON_SIZE_SM } from '@/constants/ui';
import { TrafficImpact } from '@/types/obstructions';

import { dateToIsoLocal, isoLocalToDate } from '../../../utils/dateFormat';
import type { ObstructionWizardFormState } from '../../../types';

import styles from '../Step1BasicInfo.module.css';

const IMPACT_ORDER: readonly TrafficImpact[] = [
  TrafficImpact.NoRestriction,
  TrafficImpact.ReducedLanes,
  TrafficImpact.AlternatingTraffic,
  TrafficImpact.Detour,
  TrafficImpact.FullClosure,
];

interface ScheduleSectionProps {
  form: UseFormReturnType<ObstructionWizardFormState>;
  isLoading: boolean;
}

export const ScheduleSection = memo(function ScheduleSection({
  form,
  isLoading,
}: ScheduleSectionProps) {
  const { t } = useTranslation();

  const impactOptions = useMemo(
    () =>
      IMPACT_ORDER.map((impactValue) => ({
        value: impactValue,
        label: t(`obstructions.impact.${impactValue}`),
      })),
    [t]
  );

  return (
    <FormSection>
      <div className={styles.dateRow}>
        <DateTimePicker
          label={t('obstructions.wizard.fields.plannedStart')}
          placeholder={t('obstructions.wizard.placeholders.plannedStart')}
          required
          withAsterisk
          disabled={isLoading}
          value={isoLocalToDate(form.values.plannedStartDate)}
          onChange={(value) =>
            form.setFieldValue('plannedStartDate', dateToIsoLocal(value))
          }
          error={form.errors.plannedStartDate}
          className={styles.dateField}
        />
        <DateTimePicker
          label={t('obstructions.wizard.fields.plannedEnd')}
          placeholder={t('obstructions.wizard.placeholders.plannedEnd')}
          required
          withAsterisk
          disabled={isLoading}
          value={isoLocalToDate(form.values.plannedEndDate)}
          onChange={(value) =>
            form.setFieldValue('plannedEndDate', dateToIsoLocal(value))
          }
          error={form.errors.plannedEndDate}
          className={styles.dateField}
        />
      </div>

      <Select
        label={t('obstructions.wizard.fields.impact')}
        placeholder={t('obstructions.wizard.placeholders.impact')}
        data={impactOptions}
        required
        disabled={isLoading}
        allowDeselect={false}
        leftSection={<IconRouteAltLeft size={ICON_SIZE_SM} />}
        value={form.values.impact}
        onChange={(value) => {
          if (value) form.setFieldValue('impact', value as TrafficImpact);
        }}
        error={form.errors.impact}
      />
    </FormSection>
  );
});
